import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Observable, map, of } from "rxjs";
import { Apollo, MutationResult } from "apollo-angular";
import {
  DownloadLegalEntitiesGraphObservable,
  LegalEntityFiltersGraphObservable,
  LegalEntityGraphObservable,
  SyncronizeLegalEntitiesGraphObservable
} from "../shared/models/legal-entity.model";
import { ApolloQueryResult } from "@apollo/client";
import {
  CptQL,
  QualitativeQL,
  LegalEntitiesQL,
  RiskQL
} from "../shared/graphql-operations/graph-ql.operations";
import { LazyLoadParameters } from "../features/administration/legal-entities/legal-entities.component";
import { LEGAL_FIELD } from "../shared/graphql-operations/legal-entities/legal.enum";
import { USERS_FIELD } from "../shared/graphql-operations/user/users.enum";
import {
  CptsGraphObservable,
  CptsFiltersGraphObservable,
  DownloadCptsGraphObservable
} from "../shared/models/cpt.model";
import { CPTS_FIELD } from "../shared/graphql-operations/cpt/cpt.enum";
import {
  DownloadRiskGraphObservable,
  RisksFiltersGraphObservable,
  RisksGraphObservable
} from "../shared/models/risk.model";
import { QualitativeGraphObservable } from "../shared/models/qualitative.model";
import { LPQualitative } from "../store/qualitative/qualitative.reducer";
import { EvaluationGraphObservable } from "../store/evaluation/evaluation.model";
import { CptActiveGQL, GetAuditSelfAssesmentRolesGQL, GetAuditSelfAssesmentRolesQuery, GetRolesGQL, GetRolesQuery, PagingInput, RiskQueryComplexInput, RiskSortInput } from "../../../graphql/generated";
import { GetUserRolePlannerToolGQL, GetUserRolePlannerToolQuery } from "../../../graphql-planner-tool/generated";
import { environment } from "../../environments/environment";

@Injectable({
  providedIn: "root"
})
export class AdministrationService {
  http: HttpClient = inject(HttpClient);
  private readonly apollo: Apollo = inject(Apollo);
  private readonly GetRolesGQL: GetRolesGQL = inject(GetRolesGQL);
  private readonly GetAuditSelfAssesmentRolesGQL: GetAuditSelfAssesmentRolesGQL = inject(GetAuditSelfAssesmentRolesGQL);
  private readonly GetUserRolePlannerToolGQL: GetUserRolePlannerToolGQL = inject(GetUserRolePlannerToolGQL);
  private readonly CptsActiveGQL: CptActiveGQL = inject(CptActiveGQL);
  // getDataMonitoring(
  // ): Observable<ApolloQueryResult<DataMonitoringFiltersGraphObservable> | any> {
  //   let mock = JSON.parse(JSON.stringify(DataMonitoringMock));
  //   mock.riskMap.active = Math.random() * 100
  //   mock.riskMap.inactive = Math.random() * 100
  //   return of({ data: { dataMonitoring: { data: [mock] } } })
  // }

  // getValidateDataMonitoring(
  // ): Observable<ApolloQueryResult<DataMonitoringFiltersGraphObservable> | any> {
  //   let mock = JSON.parse(JSON.stringify(DataMonitoringMock));
  //   mock.riskMap.active = Math.random() * 100
  //   mock.riskMap.inactive = Math.random() * 100
  //   return of({ data: { dataMonitoring: { data: [] } } })
  // }

  getLegalEntities(
    fields?: LazyLoadParameters,
    manageScope?: boolean
  ): Observable<ApolloQueryResult<LegalEntityFiltersGraphObservable>> {
    return this.apollo.query<LegalEntityFiltersGraphObservable>({
      query: LegalEntitiesQL.queries.table_with_filters(
        fields?.["filters"] || {},
        fields?.["search"] || "",
        manageScope || false
      ),
      fetchPolicy: "network-only",
      variables: {
        page: { pageSize: fields?.["rows"], pageNo: fields?.["page"] },
        direction: fields?.order.sortDirection,
        sortField: LEGAL_FIELD[fields?.order.sortField || "legalEntityName"],
        manageScope: manageScope || false
      }
    });
  }


  exportLegalEntities(
    fields: LazyLoadParameters
  ): Observable<MutationResult<DownloadLegalEntitiesGraphObservable>> {
    return this.apollo.mutate<DownloadLegalEntitiesGraphObservable>({
      mutation: LegalEntitiesQL.mutation.exportLegalEntities(
        fields?.["filters"] || {},
        fields?.["search"] || ""
      ),
      fetchPolicy: "network-only",
      variables: {
        direction: fields?.order.sortDirection,
        sortField: LEGAL_FIELD[fields?.order.sortField || 'null']
      }
    });
  }

  syncronizeLegalEntities(
  ): Observable<MutationResult<SyncronizeLegalEntitiesGraphObservable>> {
    return this.apollo.mutate<SyncronizeLegalEntitiesGraphObservable>({
      mutation: LegalEntitiesQL.mutation.syncronize(),
      fetchPolicy: "network-only"
    });
  }

  syncronizeRisks(
  ): Observable<MutationResult<SyncronizeLegalEntitiesGraphObservable>> {
    return this.apollo.mutate<SyncronizeLegalEntitiesGraphObservable>({
      mutation: RiskQL.mutation.syncronize(),
      fetchPolicy: "network-only"
    });
  }

  getQualitative(
    fields: LPQualitative
  ): Observable<ApolloQueryResult<QualitativeGraphObservable>> {
    return this.apollo.query<QualitativeGraphObservable>({
      query: QualitativeQL.queries.table,
      fetchPolicy: "network-only",
      variables: {
        page: { pageSize: fields?.["rows"], pageNo: fields?.["page"] }
      }
    });
  }

  deleteQualitative(id: string): Observable<any> {
    return this.apollo.mutate({
      mutation: QualitativeQL.mutations.deleteQuestion,
      variables: {
        id: id
      }
    });
  }

  deleteSection(id: string): Observable<any> {
    return this.apollo.mutate({
      mutation: QualitativeQL.mutations.deleteSection,
      variables: {
        id: id
      }
    });
  }

  createSection(
    description: string | undefined,
    name: string | undefined
  ): Observable<any> {
    return this.apollo.mutate({
      mutation: QualitativeQL.mutations.createMacroSection,
      variables: {
        input: { description: description, name: name }
      }
    });
  }

  createQuestion(
    sectionId: number,
    order: number,
    content: string,
    idGroup: any
  ): Observable<any> {
    return this.apollo.mutate({
      mutation: QualitativeQL.mutations.createQuestion(
        idGroup,
        content,
        sectionId
      )
    });
  }

  updateQuestion(
    questionId: string,
    idGroup: string,
    content: string,
    sectionId: string,
    order: number
  ): Observable<any> {
    return this.apollo.mutate({
      mutation: QualitativeQL.mutations.updateQuestion(
        questionId,
        idGroup,
        content,
        Number(sectionId),
        order
      )
    });
  }

  getLegalEntitiesFiltersNames(): Observable<
    ApolloQueryResult<LegalEntityGraphObservable>
  > {
    return this.apollo.query<LegalEntityGraphObservable>({
      query: LegalEntitiesQL.queries.names,
      fetchPolicy: "network-only"
    });
  }

  getCptsFilterNames(): Observable<ApolloQueryResult<CptsGraphObservable>> {
    return this.apollo.query<CptsGraphObservable>({
      query: CptQL.queries.names,
      fetchPolicy: "network-only"
    });
  }

  getRisksFilterNames(): Observable<ApolloQueryResult<RisksGraphObservable>> {
    return this.apollo.query<RisksGraphObservable>({
      query: RiskQL.queries.names,
      fetchPolicy: "network-only"
    });
  }

  getCpts(
    fields?: LazyLoadParameters
  ): Observable<ApolloQueryResult<CptsFiltersGraphObservable>> {
    return this.apollo.query<CptsFiltersGraphObservable>({
      query: CptQL.queries.table_with_filters(
        fields?.["filters"] || {},
        fields?.["search"] || ""
      ),
      fetchPolicy: "network-only",
      variables: {
        page: { pageSize: fields?.["rows"], pageNo: fields?.["page"] },
        direction: fields?.order.sortDirection,
        sortField: CPTS_FIELD[fields?.order?.sortField || "valueChain"]
      }
    });
    // return this.http.get<Omit<httpBurdensResponse, 'page'>>(`${this.apiUrl + this.burden}?${pagination}&${ordering}${search_query}${filters_query}`);
  }

  getActiveCpts() {
    return this.CptsActiveGQL.watch({}, { fetchPolicy: 'cache-and-network' }).valueChanges.pipe(
      map(response => response.data.cptCatalogueActiveNodes)
    )
  }

  getEvaluations(): Observable<ApolloQueryResult<EvaluationGraphObservable> | any> {
    return of({ data: { evaluations: { data: [] } } })
  }
  transformData(data: any): Record<string, unknown> {
    return {
      code: data.code,
      processName: data.processName,
      subNodes: data.subNode.map((sub: any) => this.transformData(sub)),
      status: "Completed",
      process_overall_results: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      unhautorised_activity: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      internal_theft_fraud: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      external_theft_fraud: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      system_security: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      employee_relations: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      workspace_safety: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      workspace_discrimination: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      suitability_disclosure_and_fiduciary: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      improper_business_or_market_practices: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      product_flaws: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      selection_sponsorship_and_exposure: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      advisory_activities: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      accidents_and_natural_disasters: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      human_caused_events: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      application_dysfunction: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      infrastructure_dysfunction: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      transaction_capture_execution_and_manteinance: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      client_assets_management: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      client_intake_and_documentation: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      communication: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      vendors_and_suppliers: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      trade_counterparties: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      financial_risk: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      credit_risk: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      non_life_insurance_risk: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      life_and_health_insurance_risk: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      liquidity_risk: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      strategic_risk: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      reputational_risk: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      emerging_risk: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      sustainability_risk: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      contagion_risk: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      intragroup_transaction_risk: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      concentrations_risk: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      },
      interdependency_risk: {
        iri: "L",
        irl: "M-H",
        ir: "Medium",
        ics: "AD",
        rr: "Medium"
      }
    }
  }
  exportCpts(
    fields: LazyLoadParameters
  ): Observable<MutationResult<DownloadCptsGraphObservable>> {
    return this.apollo.mutate<DownloadCptsGraphObservable>({
      mutation: CptQL.mutation.exportCpts(
        fields?.["filters"] || {},
        fields?.["search"] || ""
      ),
      fetchPolicy: "network-only",
      variables: {
        direction: fields?.order.sortDirection,
        sortField: CPTS_FIELD[fields?.order.sortField || "id"]
      }
    })
  }
  syncronizeCpts(
  ): Observable<MutationResult<SyncronizeLegalEntitiesGraphObservable>> {
    return this.apollo.mutate<SyncronizeLegalEntitiesGraphObservable>({
      mutation: CptQL.mutation.syncronize(),
      fetchPolicy: "network-only"
    });
  }

  getRisks(
    fields?: { query: RiskQueryComplexInput, sort: RiskSortInput, page: PagingInput, search?: string }
  ): Observable<ApolloQueryResult<RisksFiltersGraphObservable>> {
    return this.apollo.query<RisksFiltersGraphObservable>({
      query: RiskQL.queries.table_with_filters(
        fields!.page,
        fields!.query,
        fields!.sort,
        fields?.search
      ),
      fetchPolicy: "network-only",
      variables: {
        query: fields?.query,
        sort: fields?.sort,
        page: fields?.page
      }
    });
  }

  exportRisks(
    fields: LazyLoadParameters
  ): Observable<MutationResult<DownloadRiskGraphObservable>> {
    return this.apollo.mutate<DownloadRiskGraphObservable>({
      mutation: RiskQL.mutation.exportRisks(
        fields?.["filters"] || {},
        fields?.["search"] || ""
      ),
      fetchPolicy: "network-only",
      variables: {
        direction: fields?.order.sortDirection,
        sortField: USERS_FIELD[fields?.order.sortField || "entityName"]
      }
    });
  }

  async downloadFile(response: string, fileName: string | undefined) {
    const typeFormat = {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    };
    const link = window.document.createElement("a");
    const blob = this.base64ToBlob(response, typeFormat.type); // Cambia il tipo MIME
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName ?? "fileExport";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  }

  getProcessRiskMaps(
    fields?: LazyLoadParameters
  ): Observable<ApolloQueryResult<any> | any> {
    // return this.apollo.query<RiskMapsFiltersGraphObservable>({
    //   query: RiskMapQL.queries.table_with_filters(
    //     fields?.["filters"] || {},
    //     fields?.["search"] || ""
    //   ),
    //   fetchPolicy: "network-only",
    //   variables: {
    //     page: { pageSize: fields?.["rows"], pageNo: fields?.["page"] },
    //   },
    // });
    return of({ data: { processParameters: { data: [] } } })
  }

  setRoles(): Observable<GetRolesQuery['getRoles']> {
    return this.GetRolesGQL.watch({}, { fetchPolicy: 'no-cache' }).valueChanges.pipe(
      map(data => data.data.getRoles!)
    )
  }

  setAuditSelfRoles(): Observable<GetAuditSelfAssesmentRolesQuery['getAuditSelfAssesmentRoles']> {
    return this.GetAuditSelfAssesmentRolesGQL.watch({}, { fetchPolicy: 'no-cache' }).valueChanges.pipe(
      map(data => data.data.getAuditSelfAssesmentRoles!)
    )
  }

  setPlannerToolRole(): Observable<GetUserRolePlannerToolQuery['userRolePlannerTool']> {
    return this.GetUserRolePlannerToolGQL.fetch({}, {
      fetchPolicy: 'no-cache',
      context: { uri: `${environment.API_URL_PLANNER_TOOL}graphql` }
    }).pipe(
      map(data => data.data.userRolePlannerTool)
    );
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }
}


export interface DownloadQueryResponse {
  content: string;
  filename: string;
}

export interface SyncronizeQueryResponse {
  data: boolean[];
  message: string;
  statusCode: string;
}
